import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isApiError } from '../api/client';
import { lab, type NegativesActionRequest } from '../api/lab';
import { queryKeys } from '../api/queryKeys';
import { useAdminCall } from '../hooks/useAdminCall';
import { useConfirm } from '../hooks/useConfirm';
import type { NegativesPlan, NegativesResolution, NegativesState, Roll } from '../types/roll';
import styles from './NegativesActions.module.css';

export interface NegativesActionsProps {
  orderId: string;
  rollId: string;
  rollName: string;
  negatives: NegativesState;
  /** What the technician recorded, if anything; only that can be reopened. */
  resolution?: NegativesResolution | null;
  /** The plan, to put its action first. */
  plan?: NegativesPlan | null;
  /** Whether the lab has finished with the roll; nothing can be recorded before. */
  labDone: boolean;
  /** Offer "Send with this order" for another order of the customer's. */
  returnWithOrder?: { orderId: string; orderNumber: string };
  onDone?: (roll: Roll, message: string) => void;
}

type Choice = 'posted_back' | 'collected' | 'disposed';

const CHOICES: Record<Choice, string> = {
  posted_back: 'Posted back…',
  collected: 'Collected',
  disposed: 'Disposed of',
};

const PLAN_CHOICE: Partial<Record<NegativesPlan, Choice>> = {
  post_back: 'posted_back',
  collect: 'collected',
  dispose: 'disposed',
};

const DONE: Record<NegativesActionRequest['action'], string> = {
  posted_back: 'Recorded the negatives as posted back',
  collected: 'Recorded the negatives as collected',
  disposed: 'Recorded the negatives as disposed of',
  returned_with_order: 'Recorded the negatives as going with',
  reopen: 'Reopened the negatives of',
};

/**
 * Record what happened to a roll's negatives: posted back (with the parcel's
 * tracking if there is one), collected, disposed of, or sent with another of
 * the customer's orders; or reopen one recorded by mistake. The plan's action
 * comes first; disposal asks first, and leads once a hold has run out.
 */
export function NegativesActions({
  orderId,
  rollId,
  rollName,
  negatives,
  resolution,
  plan,
  labDone,
  returnWithOrder,
  onDone,
}: NegativesActionsProps) {
  const call = useAdminCall();
  const queryClient = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  const [posting, setPosting] = useState(false);
  const [carrier, setCarrier] = useState('Royal Mail');
  const [number, setNumber] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: (request: NegativesActionRequest) => call(() => lab.negatives(orderId, rollId, request)),
    onSuccess: (roll, request) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.rolls(orderId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.booking(orderId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.labQueue() });
      if (returnWithOrder) queryClient.invalidateQueries({ queryKey: queryKeys.admin.booking(returnWithOrder.orderId) });
      setPosting(false);
      const target = request.action === 'returned_with_order' ? ` ${returnWithOrder?.orderNumber ?? 'another order'}` : '';
      const verb = DONE[request.action];
      onDone?.(roll, request.action === 'reopen' ? `${verb} ${rollName}.` : `${verb}${target}: ${rollName}.`);
    },
    onError: (e) => setError((isApiError(e) && e.serverMessage) || 'That didn’t save. Try again.'),
  });

  function send(request: NegativesActionRequest) {
    setError(null);
    record.mutate(request);
  }

  async function choose(choice: Choice) {
    if (choice === 'posted_back') {
      setPosting(true);
      return;
    }
    if (choice === 'disposed') {
      const ok = await confirm({
        title: 'Dispose of the negatives?',
        message: `Record that the negatives of ${rollName} have been disposed of. The customer will see it.`,
        confirmLabel: 'Dispose of them',
        destructive: true,
      });
      if (!ok) return;
    }
    send({ action: choice });
  }

  function submitPosting(e: FormEvent) {
    e.preventDefault();
    send({
      action: 'posted_back',
      tracking: { carrier: carrier.trim() || undefined, number: number.trim() || undefined, url: url.trim() || undefined },
    });
  }

  if (resolution) {
    return (
      <div className={styles.actions}>
        <button type="button" className={styles.secondaryBtn} onClick={() => send({ action: 'reopen' })} disabled={record.isPending}>
          Reopen
        </button>
        {error && (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        )}
      </div>
    );
  }

  const open = ['awaiting_dispatch', 'held', 'ready_to_collect', 'awaiting_disposal', 'unknown'].includes(negatives.state);
  if (!labDone || !open) return null;

  const overdue = negatives.state === 'held' && negatives.overdue;
  const lead: Choice | undefined = overdue ? 'disposed' : plan ? PLAN_CHOICE[plan] : undefined;
  const order: Choice[] = [lead, ...(Object.keys(CHOICES) as Choice[])].filter(
    (c, i, all): c is Choice => !!c && all.indexOf(c) === i,
  );

  return (
    <div className={styles.actions}>
      {!posting && (
        <div className={styles.buttons} role="group" aria-label={`What happened to the negatives of ${rollName}`}>
          {returnWithOrder && (
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={record.isPending}
              onClick={() => send({ action: 'returned_with_order', returned_with_order_id: returnWithOrder.orderId })}
            >
              Send with {returnWithOrder.orderNumber}
            </button>
          )}
          {order.map((choice, i) => (
            <button
              key={choice}
              type="button"
              className={i === 0 && !returnWithOrder ? styles.primaryBtn : styles.secondaryBtn}
              disabled={record.isPending}
              onClick={() => void choose(choice)}
            >
              {CHOICES[choice]}
            </button>
          ))}
        </div>
      )}
      {posting && (
        <form className={styles.postForm} onSubmit={submitPosting} aria-label={`Posting the negatives of ${rollName}`}>
          <label className={styles.field}>
            <span>Carrier</span>
            <input value={carrier} onChange={(e) => setCarrier(e.target.value)} maxLength={60} />
          </label>
          <label className={styles.field}>
            <span>
              Tracking number <span className={styles.optional}>(optional)</span>
            </span>
            <input value={number} onChange={(e) => setNumber(e.target.value)} maxLength={60} autoComplete="off" />
          </label>
          <label className={styles.field}>
            <span>
              Tracking link <span className={styles.optional}>(optional)</span>
            </span>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              maxLength={500}
              placeholder="https://"
            />
          </label>
          <div className={styles.buttons}>
            <button type="submit" className={styles.primaryBtn} disabled={record.isPending}>
              {record.isPending ? 'Saving…' : 'Record posting'}
            </button>
            <button type="button" className={styles.secondaryBtn} onClick={() => setPosting(false)} disabled={record.isPending}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      {confirmDialog}
    </div>
  );
}
